import PropTypes from 'prop-types'
import { useMemo, useReducer } from 'react'
import { ThemeProvider } from 'styled-components'
import { register } from 'timeago.js'

import { customLocale } from '../PrettyDate/utils'
import { setAppDataDefaults, useGlobals } from './HsApp.utils'

import { useNavHeight } from 'shared/hooks/useNavHeight'

import { AppUI, HsAppGlobalStyle } from './HsApp.css'

import { HsAppContext } from './HsApp.context'
import { useCollapsableSidenav } from './useCollapsableSidenav'
import { Provider as HSDSProvider } from 'hsds/components/hsds'
import AriaLiveProvider from 'shared/components/AriaLive'
import NotyProvider from 'shared/components/Noty'

function HsApp({
  children,
  isFullscreen = true,
  isModal = false,
  isSidenavOnly = false,
  role,
  sidenavWidth,
  id = 'hs-app',
  scope = 'hsds-react',
}) {
  const { csrfToken, hsGlobal, appData, setHsGlobal, setAppData } = useGlobals()
  register('hs-design-standards', customLocale)

  const [isSidenavCollapsed, setSidenavCollapsedState] = useCollapsableSidenav()

  const [appKey, forceUpdate] = useReducer(x => x + 1, 0)

  const hsAppContextValue = useMemo(() => {
    return {
      appData: setAppDataDefaults(appData),
      csrfToken,
      hsGlobal,
      isSidenavCollapsed,
      setAppData,
      setHsGlobal,
      setSidenavCollapsedState,
      forceUpdate,
    }
  }, [
    appData,
    csrfToken,
    forceUpdate,
    hsGlobal,
    isSidenavCollapsed,
    setAppData,
    setHsGlobal,
    setSidenavCollapsedState,
  ])

  const navHeight = useNavHeight()

  const theme = useMemo(() => {
    const sidenavFullWidth = sidenavWidth || 250
    const sidenavCollapsedWidth = 57

    return {
      navHeight,
      sidenavCollapsedWidth,
      sidenavFullWidth,
      isSidenavCollapsed,
      isFullscreen,
      isModal,
    }
  }, [navHeight, isFullscreen, isModal, sidenavWidth, isSidenavCollapsed])

  const themeName = 'default'

  if (isSidenavOnly) {
    return (
      <HsAppContext.Provider value={hsAppContextValue}>
        <HSDSProvider themeName={themeName} scope={scope}>
          <ThemeProvider theme={theme}>{children}</ThemeProvider>
        </HSDSProvider>
      </HsAppContext.Provider>
    )
  }

  return (
    <HsAppContext.Provider value={hsAppContextValue}>
      <HsAppGlobalStyle isFullscreen={isFullscreen} />
      <HSDSProvider themeName={themeName} scope={scope}>
        <ThemeProvider theme={theme}>
          <AriaLiveProvider>
            <NotyProvider id={id}>
              <AppUI id={id} data-testid="App" key={appKey} role={role}>
                {children}
              </AppUI>
            </NotyProvider>
          </AriaLiveProvider>
        </ThemeProvider>
      </HSDSProvider>
    </HsAppContext.Provider>
  )
}

HsApp.propTypes = {
  children: PropTypes.node.isRequired,
  isFullscreen: PropTypes.bool,
  isSidenavOnly: PropTypes.bool,
  isModal: PropTypes.bool,
  role: PropTypes.string,
  sidenavWidth: PropTypes.number,
  id: PropTypes.string,
  scope: PropTypes.string,
}

export default HsApp
